import { useAccount } from "@/hooks/useAccount";
import { fmtCurrency, fmtPct } from "@/lib/api";
import PerformanceChart from "@/components/PerformanceChart";
import { Card } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Skeleton } from "@/components/ui/skeleton";
import { ArrowUpRight, ArrowDownRight, Wallet, TrendingUp, PiggyBank, Landmark } from "lucide-react";

const TYPE_COLORS = {
  "Hedge Fund": "#15803d", Stocks: "#15803d", ETF: "#0d9488", Bonds: "#2563eb",
  Crypto: "#d97706", "Mutual Funds": "#7c3aed", Cash: "#64748b", "Real Estate": "#db2777",
};

function StatCard({ label, value, sub, icon: Icon, positive, delay }) {
  return (
    <Card className="p-6 bg-white border-slate-200 shadow-[0_2px_8px_-2px_rgba(15,23,42,0.08)] hover:shadow-[0_8px_24px_-4px_rgba(15,23,42,0.12)] hover:-translate-y-0.5 transition-transform duration-300 fade-up"
      style={{ animationDelay: `${delay}ms` }}>
      <div className="flex items-center justify-between mb-4">
        <p className="overline">{label}</p>
        <div className="w-9 h-9 rounded-2xl bg-emerald-50 flex items-center justify-center text-[#15803d]">
          <Icon className="w-4 h-4" strokeWidth={1.75} />
        </div>
      </div>
      <p className="font-sans text-3xl font-bold tabular-nums tracking-tight text-slate-900">{value}</p>
      {sub != null && (
        <p className={`mt-1 text-sm font-medium tabular-nums flex items-center gap-1 ${positive ? "text-[#059669]" : "text-[#dc2626]"}`}>
          {positive ? <ArrowUpRight className="w-4 h-4" strokeWidth={2} /> : <ArrowDownRight className="w-4 h-4" strokeWidth={2} />}
          {sub}
        </p>
      )}
    </Card>
  );
}

export default function ClientDashboard() {
  const { data, isLoading } = useAccount();

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="grid gap-6 md:grid-cols-3">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-36 rounded-2xl" />)}</div>
        <Skeleton className="h-80 rounded-2xl" />
      </div>
    );
  }

  const s = data?.summary || {};
  const holdings = data?.holdings || [];
  const fund = (data?.funds || [])[0];
  const perf = data?.performance || {};
  const chartData = (perf.monthly?.length ? perf.monthly : perf.all_time) || [];
  const positive = (s.net_profit ?? s.gain ?? 0) >= 0;
  const totalCurrent = s.current_value || 0;
  const empty = holdings.length === 0;

  return (
    <div className="space-y-8" data-testid="client-dashboard">
      <div className="fade-up">
        <p className="overline mb-1">Portfolio Overview</p>
        <h1 className="font-serif text-4xl font-bold text-slate-900">Hey {data?.profile?.name?.split(" ")[0]}, welcome back</h1>
        <p className="text-slate-500 mt-1">Here's how your investments are doing today.</p>
      </div>

      {empty ? (
        <Card className="p-12 text-center bg-white border-slate-200">
          <PiggyBank className="w-10 h-10 mx-auto text-slate-300 mb-3" strokeWidth={1.25} />
          <h3 className="font-serif text-2xl text-slate-700">Your portfolio is being set up</h3>
          <p className="text-slate-500 mt-1">Your advisor will add your holdings shortly.</p>
        </Card>
      ) : (
        <>
          <div className="grid gap-6 md:grid-cols-3">
            <StatCard label="Total Portfolio Value" value={fmtCurrency(totalCurrent)}
              sub={`${fmtPct(s.gain_pct)} vs invested`} positive={(s.gain || 0) >= 0} icon={Wallet} delay={0} />
            <StatCard label="All-Time Returns" value={fmtCurrency(s.net_profit ?? s.gain)}
              sub={`${fmtPct(s.net_profit_pct ?? s.gain_pct)} generated`} positive={positive} icon={TrendingUp} delay={80} />
            <StatCard label="Invested Capital" value={fmtCurrency(s.total_deposited ?? s.invested)}
              sub={`${fmtCurrency(s.total_withdrawn || 0)} withdrawn`} positive icon={PiggyBank} delay={160} />
          </div>

          {fund && (
            <Card className="p-6 bg-[#15803d] text-white border-0 fade-up" style={{ animationDelay: "160ms" }}>
              <div className="flex flex-wrap items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-2xl bg-white/15 flex items-center justify-center">
                    <Landmark className="w-6 h-6" strokeWidth={1.75} />
                  </div>
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-[0.14em] text-white/60">{fund.code} · {fund.status}</p>
                    <h3 className="font-serif text-2xl font-bold">{fund.name}</h3>
                    <p className="text-white/70 text-sm">{fund.strategy}</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-xs uppercase tracking-wider text-white/60">Current Value</p>
                  <p className="text-3xl font-bold tabular-nums">{fmtCurrency(fund.current_value)}</p>
                </div>
              </div>
            </Card>
          )}

          <div className="grid gap-6 lg:grid-cols-3">
            <Card className="lg:col-span-2 p-6 bg-white border-slate-200 shadow-[0_2px_8px_-2px_rgba(15,23,42,0.08)] fade-up" style={{ animationDelay: "200ms" }}>
              <div className="flex items-center justify-between mb-6">
                <div>
                  <p className="overline mb-1">Performance</p>
                  <h3 className="font-serif text-2xl font-bold text-slate-900">Account value over time</h3>
                </div>
                <div className={`px-2.5 py-1 rounded-full text-xs font-semibold ${positive ? "bg-emerald-50 text-[#059669]" : "bg-red-50 text-[#dc2626]"}`}>
                  {fmtPct(s.net_profit_pct ?? s.gain_pct)}
                </div>
              </div>
              <PerformanceChart data={chartData} xMode="month" />
            </Card>

            <Card className="p-6 bg-white border-slate-200 shadow-[0_2px_8px_-2px_rgba(15,23,42,0.08)] fade-up" style={{ animationDelay: "260ms" }}>
              <p className="overline mb-1">Allocation</p>
              <h3 className="font-serif text-2xl font-bold text-slate-900 mb-6">Holdings breakdown</h3>
              <div className="space-y-4">
                {holdings.map((h, i) => {
                  const pct = totalCurrent ? (h.current_value / totalCurrent) * 100 : 0;
                  const g = h.current_value - h.invested;
                  return (
                    <div key={`${h.name}-${i}`} data-testid={`holding-${i}`}>
                      <div className="flex items-center justify-between mb-1.5">
                        <div className="flex items-center gap-2 min-w-0">
                          <span className="w-2.5 h-2.5 rounded-sm shrink-0" style={{ background: TYPE_COLORS[h.type] || "#15803d" }} />
                          <span className="text-sm font-medium text-slate-700 truncate">{h.name}</span>
                        </div>
                        <span className="text-sm font-semibold tabular-nums text-slate-900">{fmtCurrency(h.current_value)}</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <Progress value={pct} className="h-1.5 flex-1" />
                        <span className={`text-xs tabular-nums w-16 text-right ${g >= 0 ? "text-[#059669]" : "text-[#dc2626]"}`}>
                          {g >= 0 ? "+" : ""}{fmtCurrency(g)}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </Card>
          </div>
        </>
      )}
    </div>
  );
}
